import { useTranslations } from 'next-intl';
import { useState } from 'react';

import StarIcon from '@/fo/icons/base/icons/rating.svg';

import { styles } from './RatingField.styles';

export interface RatingValue {
  value_id: string;
  value: string;
}

export interface RatingFieldProps {
  onChange?: (value: number) => void;
  ratings: RatingValue[];
  ratingId: string;
  error?: boolean;
  helperText?: string;
}

const MAX_VALUE = 5;

export function RatingField({
  onChange,
  ratings,
  ratingId,
  error
}: RatingFieldProps) {
  const slots = styles();

  const t = useTranslations('review.form.rating');

  const [value, setValue] = useState(0);
  const [ratingValue, setRatingValue] = useState<RatingValue | null>(null);
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const displayValue = hoveredValue !== null ? hoveredValue : value;

  const handleStarClick = (rating: number) => {
    setValue(rating);
    setRatingValue(ratings.find((r) => r.value === rating.toString()) || null);
    onChange?.(rating);
  };

  const handleStarKeyDown = (rating: number, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStarClick(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    setHoveredValue(rating);
  };

  const handleMouseLeave = () => {
    setHoveredValue(null);
  };

  return (
    <div className={slots.base()}>
      <h3 className={slots.title()}>{t('title')}</h3>
      <div className={`${slots.description()} ${error && slots.error()}`}>
        {t('description')}
      </div>

      <div
        className={slots.ratingContainer()}
        role="radiogroup"
        aria-labelledby="rating-title"
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: MAX_VALUE }).map((_, index) => {
          const rating = index + 1;
          const isSelected = rating <= displayValue;

          return (
            <button
              key={rating}
              type="button"
              className={slots.starButton()}
              onClick={() => handleStarClick(rating)}
              onKeyDown={(e) => handleStarKeyDown(rating, e)}
              onMouseEnter={() => handleMouseEnter(rating)}
              role="radio"
              aria-checked={rating === value}
            >
              <StarIcon
                className={slots.starIcon()}
                aria-hidden="true"
              />

              {isSelected && (
                <div className={slots.starFilled()}>
                  <StarIcon
                    className={slots.starIconFilled()}
                    aria-hidden="true"
                  />
                </div>
              )}
            </button>
          );
        })}
        <input
          type="hidden"
          name="ratingId"
          value={ratingId}
        />
        <input
          type="hidden"
          name="rating"
          value={ratingValue?.value_id ?? ''}
        />
      </div>
    </div>
  );
}
